import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Alert, Button, Flex, Form, Input, Typography } from 'antd';
import FadeIn from '../FadeIn';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useForm from '../../hooks/utility/useForm';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import CountrySelect from '../form/address/CountrySelect';
import { ENVIRONMENTS } from '../../services/exports/Constants';

const GATEWAY_LABELS = { stripe: 'Stripe', adyen: 'Adyen' };

export default function BankInfoStep(props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:BankInfoStep' });
  const { buttonText, moveBackwards, moveForward } = props;

  const { company } = useSelector((state) => state.currentCompany);

  const canWriteBankInfo = company?.can_write_bank_info !== false;

  const { getValue, updating, pushUpdates } = useBusinessProfile();
  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });

  const { data, update } = useForm({
    bank_account_number: getValue('bank_account_number'),
    bank_routing_number: getValue('bank_routing_number'),
    bank_country: getValue('bank_country') ?? getValue('business_country') ?? company?.country,
    bank_statement_descriptor: getValue('bank_statement_descriptor'),
  });

  // The backend only sends this outside production; the env check keeps a misconfigured backend from surfacing it on prod.
  const testBankDetails = import.meta.env.VITE_APP_MODE !== ENVIRONMENTS.PROD ? company?.test_bank_details : null;

  const fillTestData = () => {
    ['bank_account_number', 'bank_routing_number', 'bank_country', 'bank_statement_descriptor'].forEach((field) =>
      update(field, testBankDetails[field]),
    );
  };

  const save = () => {
    if (!canWriteBankInfo) {
      return;
    }

    pushUpdates({
      data,
      onSuccess: () => moveForward && moveForward(),
      onError: ({ data }) => setErrorFeedback(data),
    });
  };

  return (
    <FadeIn>
      {renderFeedbackLabel}
      {!canWriteBankInfo && <Alert type="warning" showIcon className="mb-md" title={t('read_only_warning')} />}
      <Form layout="vertical">
        <Form.Item label={t('form.bank_account_number.label')} required>
          <Input
            disabled={!canWriteBankInfo}
            placeholder={t('form.bank_account_number.placeholder')}
            value={data.bank_account_number ?? ''}
            onChange={(e) => update('bank_account_number', e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('form.bank_routing_number.label')}>
          <Input
            disabled={!canWriteBankInfo}
            placeholder={t('form.bank_routing_number.placeholder')}
            value={data.bank_routing_number ?? ''}
            onChange={(e) => update('bank_routing_number', e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('form.bank_country.label')} required>
          <CountrySelect
            disabled={!canWriteBankInfo}
            value={data.bank_country}
            allowedCodes="*"
            onChange={(value) => update('bank_country', value)}
            placeholder={t('form.bank_country.placeholder')}
          />
        </Form.Item>
        <Form.Item
          label={t('form.bank_statement_descriptor.label')}
          tooltip={t('form.bank_statement_descriptor.tooltip')}
          required
        >
          <Input
            disabled={!canWriteBankInfo}
            placeholder={t('form.bank_statement_descriptor.placeholder')}
            value={data.bank_statement_descriptor ?? ''}
            onChange={(e) => update('bank_statement_descriptor', e.target.value)}
          />
        </Form.Item>
      </Form>
      <Flex justify="space-between" align="center" wrap gap="middle">
        <div>
          {testBankDetails && canWriteBankInfo && (
            <Flex align="center" gap="small" wrap>
              <Button type="dashed" onClick={fillTestData}>
                {t('buttons.use_test_data')}
              </Button>
              <Typography.Text type="secondary">
                {t('test_data_hint', {
                  gateways: testBankDetails.gateways.map((gateway) => GATEWAY_LABELS[gateway] ?? gateway).join(' + '),
                })}
              </Typography.Text>
            </Flex>
          )}
        </div>
        <Flex>
          <Button onClick={moveBackwards} className="mr-md">
            {t('buttons.back')}
          </Button>
          <Button type="primary" onClick={save} loading={updating} disabled={!canWriteBankInfo}>
            {buttonText ?? t('buttons.next')}
          </Button>
        </Flex>
      </Flex>
    </FadeIn>
  );
}
